import { TARIFF_CALCULATION_RULES } from '@billing/validation/tariffCalculationRules';
import type { MRT_ColumnFiltersState } from 'mantine-react-table';
import { useClaimTabFilterStore } from 'store/ClaimTabFilterStore';
import {
  getProviderStatusesForBand,
  PROVIDER_STATUS_SELECT_DEFINITIONS,
} from 'pages/claims/components/ClaimToolBarComponents/claimTabFilterConfig';
import {
  CLAIM_TARIFF_STATUS,
  claimTariffStatusEnumValues,
  READY_SUBMISSION_FILTER_ID,
  PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID,
  CLAIM_TARIFF_STATUS_BAND,
  type ClaimTariffStatusValue,
} from '@billing/validation/tariff';
import {
  Badge,
  Box,
  Flex,
  Grid,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { claimQueryKeys } from 'api/resource';
import {
  getStringConfigValue,
  isFeatureEnabled,
  subscribeToRemoteConfigUpdates,
} from 'firebase';
import { useIsMobile } from 'hooks';
import type { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import {
  IconBuildingBank,
  IconCreditCard,
  IconUserDollar,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useClaimPaginationStore } from 'store/ClaimPaginationStore';
import { useSettingsStore } from 'store/SettingsStore';
import { useCurrentUserStore } from 'store/UserStore';
import { StreamCard } from './StreamCard';
import { useProviderDashboardData } from './useProviderDashboardData';
import { safeCastNumber } from 'utils/numberUtils';
import {
  formatDateOnly,
  getDatePickerPresetRanges,
  nowISODate,
  parseDateTime,
  toISODate,
} from 'utils/dateUtils';
import { PayPeriodSummaryCard } from './PayPeriodSummaryCard';
import { AccountsReceivableDistributionCard } from './AccountsReceivableDistributionCard';
import { RevenueDistributionCard } from './RevenueDistributionCard';
import { useProviderDashboardPalette } from './useProviderDashboardPalette';

const paidTariffStatuses = claimTariffStatusEnumValues.filter(
  (status) => TARIFF_CALCULATION_RULES[status].paid
);
const billedTariffStatuses = claimTariffStatusEnumValues.filter(
  (status) =>
    TARIFF_CALCULATION_RULES[status].billed ||
    status === CLAIM_TARIFF_STATUS.READY
);

interface StreamMetricGroup {
  paid: number;
  billed: number;
  outstanding: number;
  writtenOff: number;
}

type ProviderDashboardDateBasis = 'billed-date' | 'service-date';

type ProviderDashboardTooltipKey =
  | 'paid'
  | 'billed'
  | 'outstanding'
  | 'written-off';

type ProviderDashboardTooltipConfig = Partial<
  Record<ProviderDashboardTooltipKey, string>
>;

type ProviderDashboardTooltipConfigByDateBasis = Record<
  ProviderDashboardDateBasis,
  ProviderDashboardTooltipConfig
>;

const EMPTY_PROVIDER_DASHBOARD_TOOLTIPS: ProviderDashboardTooltipConfigByDateBasis =
  {
    'billed-date': {},
    'service-date': {},
  };

const getTooltipConfig = (
  parsed: Record<string, unknown>
): ProviderDashboardTooltipConfig => ({
  paid:
    typeof parsed.paid === 'string' && parsed.paid.trim()
      ? parsed.paid.trim()
      : undefined,
  billed:
    typeof parsed.billed === 'string' && parsed.billed.trim()
      ? parsed.billed.trim()
      : undefined,
  outstanding:
    typeof parsed.outstanding === 'string' && parsed.outstanding.trim()
      ? parsed.outstanding.trim()
      : undefined,
  'written-off':
    typeof parsed['written-off'] === 'string' && parsed['written-off'].trim()
      ? parsed['written-off'].trim()
      : undefined,
});

const getDateBasisTooltipConfig = (
  parsed: Record<string, unknown>,
  keys: string[]
): ProviderDashboardTooltipConfig | undefined => {
  for (const key of keys) {
    const value = parsed[key];

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return getTooltipConfig(value as Record<string, unknown>);
    }
  }

  return undefined;
};

const getProviderDashboardTooltips =
  (): ProviderDashboardTooltipConfigByDateBasis => {
    try {
      const parsed = JSON.parse(
        getStringConfigValue('provider_dashboard_tooltips')
      ) as Record<string, unknown>;

      const legacyTooltips = getTooltipConfig(parsed);

      return {
        'billed-date':
          getDateBasisTooltipConfig(parsed, ['billed-date', 'billedDate']) ??
          legacyTooltips,
        'service-date':
          getDateBasisTooltipConfig(parsed, ['service-date', 'serviceDate']) ??
          legacyTooltips,
      };
    } catch {
      return EMPTY_PROVIDER_DASHBOARD_TOOLTIPS;
    }
  };

export const ProviderMetrics = ({
  userID,
  providerTitle,
}: {
  userID?: string;
  providerTitle?: string | null;
}) => {
  const { translate } = useTranslation();
  const queryClient = useQueryClient();
  const palette = useProviderDashboardPalette();
  const isMobile = useIsMobile(1169);
  const { providers } = useCurrentUserStore();
  const { setColumnFilters, setPageIndex, setGlobalFilter } =
    useClaimPaginationStore();
  const { providerDashboardDateBasis, setProviderDashboardPreferences } =
    useSettingsStore();
  const isAccountsReceivableEnabled = isFeatureEnabled(
    'provider_dashboard_accounts_receivable'
  );
  const [tooltipsByDateBasis, setTooltipsByDateBasis] =
    useState<ProviderDashboardTooltipConfigByDateBasis>(() =>
      getProviderDashboardTooltips()
    );

  const title = providerTitle || providers?.[0]?.title || 'Dr.';

  const {
    isLoading,
    allTimeOutstanding,
    ytdOutstandingAmount,
    ytdPaidAmount,
    ytdSubmittedAmount,
    ytdWrittenOffAmount,
    ytdPrivateOutstandingAmount,
    ytdPrivatePaidAmount,
    ytdPrivateSubmittedAmount,
    ytdPrivateWrittenOffAmount,
    ytdDirectOutstandingAmount,
    ytdDirectPaidAmount,
    ytdDirectSubmittedAmount,
    ytdDirectWrittenOffAmount,
    ytdStartDate,
    ytdEndDate,
    summaryMode,
    currentSummaryOutstandingAmount,
    currentSummaryPaidAmount,
    currentSummarySubmittedAmount,
    currentSummaryLastActivity,
    currentSummaryPeriodStart,
    currentSummaryPeriodEnd,
    currentSummaryExpectedPaymentDate,
    currentSummaryNextCutoffDate,
    lastSummaryOutstandingAmount,
    lastSummaryPaidAmount,
    lastSummarySubmittedAmount,
    lastSummaryWrittenOffAmount,
    lastSummaryPaymentDate,
    lastSummaryPeriodStart,
    lastSummaryPeriodEnd,
    dateBasis,
  } = useProviderDashboardData(userID);

  const navigate = useNavigate();

  useEffect(() => {
    setTooltipsByDateBasis(getProviderDashboardTooltips());

    return subscribeToRemoteConfigUpdates(() => {
      setTooltipsByDateBasis(getProviderDashboardTooltips());
    });
  }, []);

  const [defaultYtdStart, defaultYtdEnd] =
    getDatePickerPresetRanges().yearToDate;
  const resolvedYtdStart = parseDateTime(ytdStartDate || defaultYtdStart);
  const resolvedYtdEnd = parseDateTime(ytdEndDate || defaultYtdEnd);
  const ytdRangeLabel = `${formatDateOnly(resolvedYtdStart)} - ${formatDateOnly(
    resolvedYtdEnd
  )}`;
  const fallbackToday = parseDateTime(nowISODate());
  const parseSummaryPeriod = (value?: string | null, fallback?: DateTime) => {
    if (!value) return fallback ?? fallbackToday;

    const parsed = parseDateTime(value);
    return parsed.isValid ? parsed : fallback ?? fallbackToday;
  };
  const formatDateRange = (start?: string | null, end?: string | null) =>
    `${formatDateOnly(start)} - ${formatDateOnly(end)}`;
  const currentSummaryStart = parseSummaryPeriod(
    currentSummaryPeriodStart,
    fallbackToday
  );
  const currentSummaryEnd = parseSummaryPeriod(
    currentSummaryPeriodEnd,
    currentSummaryStart
  );
  const lastSummaryStart = parseSummaryPeriod(
    lastSummaryPeriodStart,
    currentSummaryStart
  );
  const lastSummaryEnd = parseSummaryPeriod(
    lastSummaryPeriodEnd,
    lastSummaryStart
  );

  const applyClaimFilters = ({
    tariffStatus,
    start,
    end,
  }: {
    tariffStatus?: ClaimTariffStatusValue;
    start: DateTime;
    end: DateTime;
  }) => {
    const providerStatusFilter = PROVIDER_STATUS_SELECT_DEFINITIONS.find(
      ({ statuses }) => statuses.some((status) => status === tariffStatus)
    );
    const selectedTariffStatuses = userID
      ? [tariffStatus]
      : [providerStatusFilter?.key];
    useClaimTabFilterStore.setState({
      activeTab: 'all',
      selectedTariffStatuses: selectedTariffStatuses.filter(
        (status): status is string => status !== undefined
      ),
      providerNeedsAction: false,
      tariffRowFilterMode: 'showMatchingTariffsOnly',
    });
    setGlobalFilter('');
    setPageIndex(0);
    const filters: MRT_ColumnFiltersState = [
      {
        id: isServiceDateSelected ? 'serviceStart' : 'submissions.submittedAt',
        value: [toISODate(start), toISODate(end)],
      },
    ];

    if (tariffStatus) {
      filters.unshift({
        id: 'tariffs.status',
        value: [tariffStatus],
      });
    }

    if (userID) filters.push({ id: 'provider.userID', value: userID });
    setColumnFilters(filters);
    navigate('/claims');
  };

  const openPaidClaims = (
    start: DateTime,
    end: DateTime,
    payorType?: 'PUBLIC' | 'PRIVATE' | 'DIRECT'
  ) => {
    useClaimTabFilterStore.setState({
      activeTab: 'all',
      selectedTariffStatuses: paidTariffStatuses,
      providerNeedsAction: false,
      tariffRowFilterMode: 'showMatchingTariffsOnly',
    });
    const filters: MRT_ColumnFiltersState = [
      { id: 'tariffs.status', value: paidTariffStatuses },
      {
        id: isServiceDateSelected ? 'serviceStart' : 'remittances.parsedAt',
        value: [toISODate(start), toISODate(end)],
      },
    ];
    if (payorType) filters.push({ id: 'payor.type', value: payorType });
    if (userID) filters.push({ id: 'provider.userID', value: userID });
    setGlobalFilter('');
    setPageIndex(0);
    setColumnFilters(filters);
    void queryClient.invalidateQueries({ queryKey: claimQueryKeys.all });
    navigate('/claims');
  };

  const openBilledClaims = (
    start: DateTime,
    end: DateTime,
    payorType?: 'PUBLIC' | 'PRIVATE' | 'DIRECT'
  ) => {
    useClaimTabFilterStore.setState({
      activeTab: 'all',
      selectedTariffStatuses: billedTariffStatuses,
      providerNeedsAction: false,
      tariffRowFilterMode: 'showMatchingTariffsOnly',
    });
    const filters: MRT_ColumnFiltersState = [
      { id: 'tariffs.status', value: billedTariffStatuses },
      {
        id: isServiceDateSelected
          ? 'serviceStart'
          : PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID,
        value: [toISODate(start), toISODate(end)],
      },
      { id: READY_SUBMISSION_FILTER_ID, value: 'true' },
    ];
    if (payorType) filters.push({ id: 'payor.type', value: payorType });
    if (userID) filters.push({ id: 'provider.userID', value: userID });
    setGlobalFilter('');
    setPageIndex(0);
    setColumnFilters(filters);
    navigate('/claims');
  };

  const openLastSummaryPaidClaims = () => {
    if (summaryMode === 'pay-period' && !isServiceDateSelected) {
      if (!lastSummaryPaymentDate) return;
      const paymentDate = parseDateTime(lastSummaryPaymentDate);
      const paymentWindowStart = lastSummaryEnd.plus({ days: 1 });
      const paymentWindowEnd = paymentDate.minus({ days: 1 });
      if (paymentDate.isValid && paymentWindowEnd >= paymentWindowStart) {
        openPaidClaims(paymentWindowStart, paymentWindowEnd);
      }
      return;
    }
    openPaidClaims(lastSummaryStart, lastSummaryEnd);
  };

  const openOutstandingClaims = (
    start: DateTime,
    end: DateTime,
    payorType?: 'PUBLIC' | 'PRIVATE' | 'DIRECT'
  ) => {
    useClaimTabFilterStore.setState({
      activeTab: userID ? 'all' : 'outstanding',
      selectedTariffStatuses: userID
        ? getProviderStatusesForBand(CLAIM_TARIFF_STATUS_BAND.OUTSTANDING)
        : [],
      providerNeedsAction: false,
      tariffRowFilterMode: 'showMatchingTariffsOnly',
    });
    const filters: MRT_ColumnFiltersState = [
      {
        id: isServiceDateSelected
          ? 'serviceStart'
          : PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID,
        value: [toISODate(start), toISODate(end)],
      },
      ...getProviderStatusesForBand(CLAIM_TARIFF_STATUS_BAND.OUTSTANDING).map(
        (status) => ({ id: 'tariffs.status', value: status })
      ),
      { id: READY_SUBMISSION_FILTER_ID, value: 'true' },
    ];
    if (payorType) filters.push({ id: 'payor.type', value: payorType });
    if (userID) filters.push({ id: 'provider.userID', value: userID });
    setGlobalFilter('');
    setColumnFilters(filters);
    setPageIndex(0);
    navigate('/claims');
  };

  const publicMetrics: StreamMetricGroup = {
    paid: safeCastNumber(ytdPaidAmount),
    billed: safeCastNumber(ytdSubmittedAmount),
    outstanding: safeCastNumber(ytdOutstandingAmount),
    writtenOff: safeCastNumber(ytdWrittenOffAmount),
  };

  const thirdPartyMetrics: StreamMetricGroup = {
    paid: safeCastNumber(ytdPrivatePaidAmount),
    billed: safeCastNumber(ytdPrivateSubmittedAmount),
    outstanding: safeCastNumber(ytdPrivateOutstandingAmount),
    writtenOff: safeCastNumber(ytdPrivateWrittenOffAmount),
  };

  const directPayMetrics: StreamMetricGroup = {
    paid: safeCastNumber(ytdDirectPaidAmount),
    billed: safeCastNumber(ytdDirectSubmittedAmount),
    outstanding: safeCastNumber(ytdDirectOutstandingAmount),
    writtenOff: safeCastNumber(ytdDirectWrittenOffAmount),
  };

  const getStreamTotal = (metrics: StreamMetricGroup) => metrics.paid;

  const publicTotal = getStreamTotal(publicMetrics);
  const thirdPartyTotal = getStreamTotal(thirdPartyMetrics);
  const directPayTotal = getStreamTotal(directPayMetrics);
  const overallTotal = publicTotal + thirdPartyTotal + directPayTotal;
  const distributionTotal = overallTotal || 1;

  const showMonthSummary = summaryMode === 'month';
  const topCardSpan = { base: 12, md: title === 'NP.' ? 12 : 6 };
  const activityDateLabel =
    dateBasis === 'service-date'
      ? translate(
          'pages:providerDashboard.metrics.rows.latestServiceDate',
          'Latest Service Date'
        )
      : translate(
          'pages:providerDashboard.card.rows.lastSubmissionUpload',
          'Last Submission Upload'
        );
  const currentSummaryTitle = showMonthSummary
    ? translate(
        'pages:providerDashboard.metrics.titles.thisMonth',
        'This Month'
      )
    : translate(
        'pages:providerDashboard.metrics.titles.currentPayPeriod',
        'Current Pay Period'
      );
  const currentSummaryBadge = formatDateRange(
    currentSummaryPeriodStart,
    currentSummaryPeriodEnd
  );
  const selectedDateBasis = providerDashboardDateBasis ?? dateBasis;
  const isServiceDateSelected = selectedDateBasis === 'service-date';
  const selectedTooltips = tooltipsByDateBasis[selectedDateBasis];
  const getMetricTooltip = (key: ProviderDashboardTooltipKey) =>
    selectedTooltips?.[key];
  const lastSummaryTitle = showMonthSummary
    ? translate(
        'pages:providerDashboard.metrics.titles.lastMonth',
        'Last Month'
      )
    : translate(
        'pages:providerDashboard.metrics.titles.lastPayPeriod',
        'Last Pay Period'
      );
  const lastSummaryBadge = formatDateRange(
    lastSummaryPeriodStart,
    lastSummaryPeriodEnd
  );
  const lastSummaryPaymentLabel = lastSummaryPaymentDate
    ? `${translate(
        'pages:providerDashboard.card.badges.paidOn',
        'Paid On'
      )} ${formatDateOnly(lastSummaryPaymentDate)}`
    : translate(
        'pages:providerDashboard.card.badges.paymentDateUnavailable',
        'Payment Date Unavailable'
      );
  const currentSummary = {
    title: currentSummaryTitle,
    badge: currentSummaryBadge,
    rows:
      summaryMode === 'pay-period'
        ? [
            {
              label: translate(
                'pages:providerDashboard.metrics.rows.billed',
                'Billed'
              ),
              value: safeCastNumber(currentSummarySubmittedAmount),
              tooltip: getMetricTooltip('billed'),
              onClick: () =>
                openBilledClaims(currentSummaryStart, currentSummaryEnd),
              color: palette.secondaryText,
            },
            {
              label: translate(
                'pages:providerDashboard.card.rows.expectedPaymentDate',
                'Expected Payment Date'
              ),
              value: formatDateOnly(currentSummaryExpectedPaymentDate),
              color: palette.secondaryText,
            },
            {
              label: translate(
                'pages:providerDashboard.metrics.rows.nextCutoffDate',
                'Next Cutoff Date'
              ),
              value: formatDateOnly(currentSummaryNextCutoffDate),
              color: palette.secondaryText,
            },
            {
              label: activityDateLabel,
              value: formatDateOnly(currentSummaryLastActivity),
              onClick: () =>
                applyClaimFilters({
                  start: currentSummaryStart,
                  end: currentSummaryEnd,
                }),
              color: palette.secondaryText,
            },
          ]
        : [
            {
              label: translate(
                'pages:providerDashboard.metrics.rows.billed',
                'Billed'
              ),
              value: safeCastNumber(currentSummarySubmittedAmount),
              tooltip: getMetricTooltip('billed'),
              onClick: () =>
                openBilledClaims(currentSummaryStart, currentSummaryEnd),
              color: palette.secondaryText,
            },
            {
              label: translate(
                'pages:providerDashboard.card.rows.paid',
                'Paid'
              ),
              value: safeCastNumber(currentSummaryPaidAmount),
              tooltip: getMetricTooltip('paid'),
              onClick: () =>
                openPaidClaims(currentSummaryStart, currentSummaryEnd),
              color: palette.secondaryText,
            },
            {
              label: translate(
                'pages:providerDashboard.metrics.rows.outstanding',
                'Outstanding'
              ),
              tooltip: getMetricTooltip('outstanding'),
              value: safeCastNumber(currentSummaryOutstandingAmount),
              onClick: () =>
                openOutstandingClaims(currentSummaryStart, currentSummaryEnd),
              color: palette.secondaryText,
            },
            {
              label: activityDateLabel,
              value: formatDateOnly(currentSummaryLastActivity),
              onClick: () =>
                applyClaimFilters({
                  start: currentSummaryStart,
                  end: currentSummaryEnd,
                }),
              color: palette.secondaryText,
            },
          ],
  };

  const lastSummary = {
    title: lastSummaryTitle,
    badge: lastSummaryBadge,
    badgeColor: '#1C3354',
    rows: [
      {
        label: translate(
          'pages:providerDashboard.metrics.rows.billed',
          'Billed'
        ),
        value: safeCastNumber(lastSummarySubmittedAmount),
        tooltip: getMetricTooltip('billed'),
        onClick: () => openBilledClaims(lastSummaryStart, lastSummaryEnd),
        color: palette.secondaryText,
      },
      {
        label: translate('pages:providerDashboard.card.rows.paid', 'Paid'),
        value: safeCastNumber(lastSummaryPaidAmount),
        tooltip: showMonthSummary
          ? getMetricTooltip('paid')
          : translate(
              'pages:providerDashboard.card.tooltips.lastPayPeriodPaid',
              'Before overhead. Paid includes public payor, third-party and patient payments for the displayed pay period. Some payments may still be in transit, total may differ from the amount deposited into your account. Click on the paid amount, then filter by payor to see where each payment comes from.'
            ),
        onClick: openLastSummaryPaidClaims,
        color: palette.secondaryText,
      },
      {
        label: translate(
          'pages:providerDashboard.metrics.rows.outstanding',
          'Outstanding'
        ),
        tooltip: getMetricTooltip('outstanding'),
        value: safeCastNumber(lastSummaryOutstandingAmount),
        onClick: () => openOutstandingClaims(lastSummaryStart, lastSummaryEnd),
        color: palette.secondaryText,
      },
      {
        label: translate(
          'pages:providerDashboard.card.rows.writtenOff',
          'Written-Off'
        ),
        value: safeCastNumber(lastSummaryWrittenOffAmount),
        tooltip: getMetricTooltip('written-off'),
        onClick: () =>
          applyClaimFilters({
            tariffStatus: CLAIM_TARIFF_STATUS.WRITTEN_OFF,
            start: lastSummaryStart,
            end: lastSummaryEnd,
          }),
        color: palette.secondaryText,
      },
    ],
  };

  return (
    <>
      <Stack gap={20} pb="sm">
        <Flex justify="flex-start">
          <Stack gap={6} align="flex-start">
            <Text
              size="sm"
              fw={700}
              c={palette.primaryText}
              data-testid="provider-metrics-date-basis-label"
            >
              {translate(
                'pages:providerDashboard.metrics.dateBasis.toggleTitle',
                'View metrics by'
              )}
            </Text>
            <UnstyledButton
              type="button"
              onClick={() => {
                const nextDateBasis = isServiceDateSelected
                  ? 'billed-date'
                  : 'service-date';
                const nextSummaryMode =
                  nextDateBasis === 'service-date' ? 'month' : 'pay-period';

                setProviderDashboardPreferences({
                  dateBasis: nextDateBasis,
                  summaryMode: nextSummaryMode,
                });
              }}
              aria-label={translate(
                'pages:providerDashboard.metrics.dateBasis.toggleAriaLabel',
                'Toggle provider metrics between Payment Date and Service Date'
              )}
              data-testid="provider-metrics-date-basis-toggle"
              style={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                padding: '6px 8px',
                borderRadius: 999,
                background: palette.toggleBackground,
                border: `1px solid ${palette.toggleBorder}`,
                boxShadow: palette.toggleShadow,
                cursor: 'pointer',
              }}
            >
              <Box
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  top: 6,
                  bottom: 6,
                  left: isServiceDateSelected ? 'calc(50% + 4px)' : 6,
                  width: 'calc(50% - 10px)',
                  borderRadius: 999,
                  background: palette.toggleThumbBackground,
                  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.28)',
                  transition: 'left 180ms ease',
                }}
              />
              <Text
                size="sm"
                fw={700}
                c={
                  isServiceDateSelected
                    ? palette.toggleInactiveText
                    : palette.toggleThumbText
                }
                style={{
                  position: 'relative',
                  zIndex: 1,
                  minWidth: 108,
                  textAlign: 'center',
                }}
              >
                {translate(
                  'pages:providerDashboard.metrics.dateBasis.billedDate',
                  'Billed Date'
                )}
              </Text>
              <Text
                size="sm"
                fw={700}
                c={
                  isServiceDateSelected
                    ? palette.toggleThumbText
                    : palette.toggleInactiveText
                }
                style={{
                  position: 'relative',
                  zIndex: 1,
                  minWidth: 108,
                  textAlign: 'center',
                }}
              >
                {translate(
                  'pages:providerDashboard.metrics.dateBasis.serviceDate',
                  'Service Date'
                )}
              </Text>
            </UnstyledButton>
            <Text size="xs" c={palette.mutedText} maw={420}>
              {isServiceDateSelected
                ? translate(
                    'pages:providerDashboard.metrics.dateBasis.toggleHintPayment',
                    'Groups your revenue by the date each service was performed. Best for tracking clinical activity.'
                  )
                : translate(
                    'pages:providerDashboard.metrics.dateBasis.toggleHintService',
                    'Paid claims are grouped by their paid date. Best for cash flow and accounting.'
                  )}
            </Text>
          </Stack>
        </Flex>
        <Grid gutter="md">
          {title !== 'NP.' && (
            <Grid.Col span={topCardSpan}>
              <PayPeriodSummaryCard
                title={currentSummary.title}
                badge={currentSummary.badge}
                badgeColor="#1C3354"
                isLoading={isLoading}
                rows={currentSummary.rows}
              />
            </Grid.Col>
          )}

          <Grid.Col span={topCardSpan}>
            <PayPeriodSummaryCard
              title={lastSummary.title}
              badge={lastSummary.badge}
              badgeColor={lastSummary.badgeColor}
              topRightBadge={
                !showMonthSummary && (
                  <Badge
                    color={palette.isDark ? 'lightgreen' : 'darkgreen'}
                    variant="outline"
                    radius="xl"
                    size="md"
                    tt="none"
                  >
                    {lastSummaryPaymentLabel}
                  </Badge>
                )
              }
              isLoading={isLoading}
              rows={lastSummary.rows}
            />
          </Grid.Col>
        </Grid>

        <Grid gutter="md">
          <Grid.Col
            span={{ base: 12, md: isAccountsReceivableEnabled ? 6 : 12 }}
          >
            <RevenueDistributionCard
              isLoading={isLoading}
              publicTotal={publicTotal}
              thirdPartyTotal={thirdPartyTotal}
              directPayTotal={directPayTotal}
              distributionTotal={distributionTotal}
              overallTotal={overallTotal}
              dateBasis={dateBasis}
              ytdRangeLabel={ytdRangeLabel}
            />
          </Grid.Col>
          {isAccountsReceivableEnabled && (
            <Grid.Col span={{ base: 12, md: 6 }}>
              <AccountsReceivableDistributionCard
                isLoading={isLoading}
                outstandingSummaryData={allTimeOutstanding}
                onOutstandingClick={(payorType) =>
                  openOutstandingClaims(
                    parseDateTime('1900-01-01'),
                    resolvedYtdEnd,
                    payorType
                  )
                }
                isMobile={isMobile}
              />
            </Grid.Col>
          )}
        </Grid>
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, md: 4 }}>
            <StreamCard
              title={translate(
                'pages:providerDashboard.card.sections.publicPay',
                'Public Pay'
              )}
              subtitle={translate(
                'pages:providerDashboard.metrics.subtitles.publicPay',
                'Provincial / Government'
              )}
              accent="#59C79A"
              icon={<IconBuildingBank size={20} />}
              total={publicTotal}
              rangeLabel={ytdRangeLabel}
              isLoading={isLoading}
              dateBasis={dateBasis}
              rows={[
                {
                  label: translate(
                    'pages:providerDashboard.metrics.rows.billed',
                    'Billed'
                  ),
                  value: publicMetrics.billed,
                  tooltip: getMetricTooltip('billed'),
                  onClick: () =>
                    openBilledClaims(
                      resolvedYtdStart,
                      resolvedYtdEnd,
                      'PUBLIC'
                    ),
                  color: '#4B96F1',
                },
                {
                  label: translate(
                    'pages:providerDashboard.card.rows.paid',
                    'Paid'
                  ),
                  value: publicMetrics.paid,
                  tooltip: getMetricTooltip('paid'),
                  onClick: () =>
                    openPaidClaims(resolvedYtdStart, resolvedYtdEnd, 'PUBLIC'),
                  color: '#59C79A',
                },
                {
                  label: translate(
                    'pages:providerDashboard.metrics.rows.outstanding',
                    'Outstanding'
                  ),
                  value: publicMetrics.outstanding,
                  onClick: () =>
                    openOutstandingClaims(
                      resolvedYtdStart,
                      resolvedYtdEnd,
                      'PUBLIC'
                    ),
                  tooltip: getMetricTooltip('outstanding'),
                  color: '#F0B23A',
                },
                {
                  label: translate(
                    'pages:providerDashboard.card.rows.writtenOff',
                    'Written-Off'
                  ),
                  value: publicMetrics.writtenOff,
                  tooltip: getMetricTooltip('written-off'),
                  onClick: () =>
                    applyClaimFilters({
                      tariffStatus: CLAIM_TARIFF_STATUS.WRITTEN_OFF,
                      start: resolvedYtdStart,
                      end: resolvedYtdEnd,
                    }),
                  color: '#9EA3AF',
                },
              ]}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <StreamCard
              title={translate(
                'pages:providerDashboard.card.sections.thirdParty',
                'Third-Party'
              )}
              subtitle={translate(
                'pages:providerDashboard.metrics.subtitles.thirdParty',
                'Insurance Payors'
              )}
              accent="#4B96F1"
              icon={<IconCreditCard size={20} />}
              total={thirdPartyTotal}
              rangeLabel={ytdRangeLabel}
              isLoading={isLoading}
              dateBasis={dateBasis}
              rows={[
                {
                  label: translate(
                    'pages:providerDashboard.metrics.rows.billed',
                    'Billed'
                  ),
                  value: thirdPartyMetrics.billed,
                  tooltip: getMetricTooltip('billed'),
                  color: '#4B96F1',
                },
                {
                  label: translate(
                    'pages:providerDashboard.card.rows.paid',
                    'Paid'
                  ),
                  value: thirdPartyMetrics.paid,
                  tooltip: getMetricTooltip('paid'),
                  color: '#59C79A',
                },
                {
                  label: translate(
                    'pages:providerDashboard.metrics.rows.outstanding',
                    'Outstanding'
                  ),
                  value: thirdPartyMetrics.outstanding,
                  onClick: () =>
                    openOutstandingClaims(
                      resolvedYtdStart,
                      resolvedYtdEnd,
                      'PRIVATE'
                    ),
                  tooltip: getMetricTooltip('outstanding'),
                  color: '#F0B23A',
                },
                {
                  label: translate(
                    'pages:providerDashboard.card.rows.writtenOff',
                    'Written-Off'
                  ),
                  value: thirdPartyMetrics.writtenOff,
                  tooltip: getMetricTooltip('written-off'),
                  color: '#9EA3AF',
                },
              ]}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <StreamCard
              title={translate(
                'pages:providerDashboard.card.sections.directPay',
                'Direct Pay'
              )}
              subtitle={translate(
                'pages:providerDashboard.metrics.subtitles.directPay',
                'Patient Invoicing'
              )}
              accent="#F0B23A"
              icon={<IconUserDollar size={20} />}
              total={directPayTotal}
              rangeLabel={ytdRangeLabel}
              isLoading={isLoading}
              dateBasis={dateBasis}
              rows={[
                {
                  label: translate(
                    'pages:providerDashboard.metrics.rows.billed',
                    'Billed'
                  ),
                  value: directPayMetrics.billed,
                  tooltip: getMetricTooltip('billed'),
                  color: '#4B96F1',
                },
                {
                  label: translate(
                    'pages:providerDashboard.card.rows.paid',
                    'Paid'
                  ),
                  value: directPayMetrics.paid,
                  tooltip: getMetricTooltip('paid'),
                  color: '#59C79A',
                },
                {
                  label: translate(
                    'pages:providerDashboard.metrics.rows.outstanding',
                    'Outstanding'
                  ),
                  value: directPayMetrics.outstanding,
                  onClick: () =>
                    openOutstandingClaims(
                      resolvedYtdStart,
                      resolvedYtdEnd,
                      'DIRECT'
                    ),
                  tooltip: getMetricTooltip('outstanding'),
                  color: '#F0B23A',
                },
                {
                  label: translate(
                    'pages:providerDashboard.card.rows.writtenOff',
                    'Written-Off'
                  ),
                  value: directPayMetrics.writtenOff,
                  tooltip: getMetricTooltip('written-off'),
                  color: '#9EA3AF',
                },
              ]}
            />
          </Grid.Col>
        </Grid>
      </Stack>
    </>
  );
};
